import React from "react";
import { Card } from "../../components/ui/card";
import { ArrowUp, ArrowDown } from "lucide-react";
import type { MetricCard } from "../../../types/admin";

interface SectionCardsProps {
  metrics: MetricCard[];
}

export function SectionCards({ metrics }: SectionCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {metrics.map((metric, index) => (
        <MetricCardComponent key={index} metric={metric} />
      ))}
    </div>
  );
}

function MetricCardComponent({ metric }: { metric: MetricCard }) {
  const { label, value, trend, icon, description } = metric;
  const hasTrend = trend !== undefined;
  const isPositive = trend && trend > 0;
  const isNegative = trend && trend < 0;

  return (
    <Card className="rounded-xl border-0 bg-sand p-6 shadow-card transition-all duration-200 hover:shadow-cardHover dark:bg-darkcard">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
            {label}
          </div>
          <div className="text-3xl font-bold text-martinique dark:text-white">
            {value}
          </div>
          {description && (
            <p className="mt-2 text-xs text-martinique/50 dark:text-white/50">
              {description}
            </p>
          )}
        </div>
        {icon && (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-coral/10 text-coral">
            {icon}
          </div>
        )}
      </div>

      {hasTrend && (
        <div className="mt-4 flex items-center gap-1">
          {isPositive && (
            <>
              <ArrowUp className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-600">
                +{Math.abs(trend).toFixed(1)}%
              </span>
            </>
          )}
          {isNegative && (
            <>
              <ArrowDown className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-600">
                {trend.toFixed(1)}%
              </span>
            </>
          )}
          {!isPositive && !isNegative && (
            <span className="text-sm font-medium text-martinique/60 dark:text-white/60">
              0%
            </span>
          )}
          <span className="ml-1 text-xs text-martinique/50 dark:text-white/50">
            from last period
          </span>
        </div>
      )}
    </Card>
  );
}
